
import { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import { getAccounts } from '../../../services/accountsService';
import SearchTable from '../../../components/search-table/SearchTable';
import { StyledSystem } from './systems.styled';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '../../../helpers/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { AccountStructure } from '../../../models/accounts-structure';
import { Account, AccountSchema, AccountSearch } from '../../../models/account';
import { RootState } from '../../../redux/store';
import { Skeleton } from '@mui/material';
import { CanceledError } from 'axios';
import ListItemIcon from '@mui/material/ListItemIcon';
import MovingIcon from '@mui/icons-material/Moving';
import EditIcon from '@mui/icons-material/Edit';
import { setSystemSearch } from '../../../redux/features/browse';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../../services/errorHandling';
import { useGetAccountStructureQuery } from '../../../redux/api/accounts-structure.api';
// import { useGetAccountsByTypeQuery } from '../../../redux/api/accounts.api';


interface SystemProps {
    searchFormData?: AccountSearch;
}

function Systems({ searchFormData }: SystemProps) {

    
    const { showBoundary } = useErrorBoundary();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const { data: systemStructure } = useGetAccountStructureQuery("קופה");

    const systemSearch = useSelector((state: RootState) => state.browse.systemSearch)

    const [data, setData] = useState<Account[]>([]);
    const [searchStructure, setSearchStructure] = useState<AccountStructure[]>();
    const [tableStructure, setTableStructure] = useState<AccountStructure[]>();
    const [sortKey, setSortKey] = useState<string>('id');
    const [sortMethod, setSortMethod] = useState<string>('DESC');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const options = [
        {
            label: "עריכה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
            href: ""
        },
        {
            label: "תנועות",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><MovingIcon fontSize="small" /></ListItemIcon>,
            href: "journal-entries"
        },
    ];


    const systemSearchForm = useForm<AccountSearch>({defaultValues:{
        "account_type": "מערכת",
        ...systemSearch,
        ...searchFormData
    }});


    useEffect(() => {
        setSearchStructure(systemStructure?.filter(field => field.show_search))
        setTableStructure(systemStructure?.filter(field => field.show_browse))
    }, [systemStructure]);


    useEffect(() => {
        if (sortKey && sortMethod) {
            systemSearchForm.handleSubmit(onSearch)();
        }
    }, [sortKey, sortMethod]);


    useEffect(() => {
        systemSearchForm.watch(() => {
            systemSearchForm.handleSubmit(onSearch)();
        })
    }, [systemSearchForm.watch]);



    function handleSortKeyChanged(value: string) {
        setSortKey(value);
    }


    function handleSortMethodChanged(value: string) {
        setSortMethod(value);
    }


    const onSearch: SubmitHandler<AccountSearch> = (data) => {
        dispatch(setSystemSearch(data));
        getData(data);
    }


    async function getData(data: AccountSearch) {

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const responseData = await getAccounts(deleteEmptyFieldsFromObject(data), sortKey || 'id', "10", sortMethod || 'ASC', ["customData", "entries.document.documentSettings"], abortControllerRef.current.signal);
            const systemsParse = z.array(AccountSchema).parse(responseData);
            setData(systemsParse || []);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof CanceledError) {
                return;
            }
            handleApiErrors(error, showBoundary);
        }
    }


    function createState(){
        return {
            ...location.state,
        }
    }


    function createBreadcrumbData(state: any){

        const breadcrumbData = [
            {
                name: "כרטסות",
            },
            {
                name: "מערכת",
                link: "/system",
            },
        ];

        return breadcrumbData;
    }


    function editSearchStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/system/structure/search', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה חיפוש" }]
            }
        });
    }


    function editBrowseStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/system/structure/browse', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה דפדוף" }]
            }
        });
    }


    function reset() {

        const values = setAllValuesToNull(systemSearchForm.getValues());

        for (const key in values) {
            if (values.hasOwnProperty(key)) {
                systemSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
            }
        }

        systemSearchForm.setValue("account_type", "מערכת")
    }

    
    return (
        <StyledSystem>
            {searchStructure ?
                <SearchTable structure={searchStructure} setStructure={editSearchStructure} reset={reset} useForm={systemSearchForm} style={{ backgroundColor: "rgb(35, 48, 68)", padding: 5, marginButton: 20 }} /> :
                <Skeleton variant="rectangular" height={100} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
            {tableStructure ?
                <DataTable structure={tableStructure} setStructure={editBrowseStructure} sortKey={sortKey} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options} /> :
                <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
        </StyledSystem>
    );
}

export default Systems;